import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Languages, Download, Upload, FileText, LogOut, MessageSquare, Send,
  Loader2, CheckCircle, AlertCircle, Eye, UserCheck, PackageCheck, X
} from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_CONFIG = {
  submitted: { label: 'Submitted', cls: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Send },
  ai_processing: { label: 'AI Processing', cls: 'bg-violet-100 text-violet-700 border-violet-200', icon: Loader2 },
  submitted_to_vendor: { label: 'Submitted to Vendor', cls: 'bg-sky-100 text-sky-700 border-sky-200', icon: UserCheck },
  in_review: { label: 'In Review', cls: 'bg-amber-100 text-amber-700 border-amber-200', icon: Eye },
  ready_for_download: { label: 'Ready for Download', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: PackageCheck },
  completed: { label: 'Completed', cls: 'bg-blue-100 text-blue-700 border-blue-200', icon: CheckCircle },
  changes_requested: { label: 'Changes Requested', cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: AlertCircle }
};

const MIME_BY_EXT = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain'
};

function downloadBase64(fileName, base64, mime = 'application/octet-stream') {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const blob = new Blob([arr], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

async function downloadFileById(fileId) {
  const res = await axios.get(`${API}/translations/file/${fileId}`, { headers: authHeaders() });
  const { file_name, file_type, data } = res.data;
  downloadBase64(file_name, data, MIME_BY_EXT[file_type] || 'application/octet-stream');
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: FileText };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

// ────────────────────────────────────────────────────────────────
// Job Detail Drawer
// ────────────────────────────────────────────────────────────────
function JobDetail({ job, onClose, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [marking, setMarking] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [localJob, setLocalJob] = useState(job);

  useEffect(() => { setLocalJob(job); }, [job]);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/translations/request/${job.id}`, { headers: authHeaders() });
      setLocalJob(res.data);
      onUpdated?.();
    } catch (e) { console.error(e); }
  };

  const handleStatusChange = async (status) => {
    setError('');
    try {
      await axios.patch(`${API}/translations/${job.id}`, { status }, { headers: authHeaders() });
      refresh();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to update status'); }
  };

  const handleUpload = async (files) => {
    setError('');
    setUploading(true);
    for (const f of files) {
      const ext = f.name.split('.').pop().toLowerCase();
      if (!['pdf', 'doc', 'docx', 'txt'].includes(ext)) {
        setError(`Unsupported file: ${f.name}`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', f);
      try {
        await axios.post(`${API}/translations/${job.id}/files`, fd, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
        });
      } catch (e) {
        setError(e.response?.data?.detail || 'Upload failed');
      }
    }
    setUploading(false);
    refresh();
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/translations/${job.id}/notes`, { note }, { headers: authHeaders() });
      setNote('');
      refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to add note');
    } finally { setSavingNote(false); }
  };

  const markReady = async () => {
    setError('');
    setMarking(true);
    try {
      await axios.post(`${API}/translations/${job.id}/mark-ready`, {}, { headers: authHeaders() });
      refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to mark ready');
    } finally { setMarking(false); }
  };

  const original = (localJob.files || []).filter(f => f.file_role === 'original');
  const translated = (localJob.files || []).filter(f => f.file_role === 'translated');

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="vendor-job-detail">
      <div className="bg-white w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-xs text-slate-500 font-mono">#{localJob.id.slice(0, 8)}</div>
            <h3 className="text-lg font-bold text-slate-900">{localJob.source_language} → {localJob.target_language}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="vendor-job-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Meta */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Client / Firm</div><div className="text-sm font-medium text-slate-900 mt-1">{localJob.firm_name || '-'}</div></div>
            <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Status</div><div className="mt-1"><StatusBadge status={localJob.status} /></div></div>
            <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Type</div><div className="text-sm font-medium text-slate-900 mt-1">{localJob.translation_type === 'ai' ? 'AI Translation' : 'AI + Human Review'}</div></div>
            <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Word Count</div><div className="text-sm font-medium text-slate-900 mt-1">{localJob.total_word_count ? `${localJob.total_word_count.toLocaleString()} words` : 'Manual review'}</div></div>
            <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Urgency</div><div className="text-sm font-medium text-slate-900 mt-1 capitalize">{localJob.urgency}</div></div>
            <div className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-500">Submitted</div><div className="text-sm font-medium text-slate-900 mt-1">{new Date(localJob.created_at).toLocaleString()}</div></div>
          </div>

          {localJob.translation_type === 'ai_human' && (
            <div className={`rounded-xl p-4 border ${localJob.per_word_rate_usd != null ? 'bg-gradient-to-br from-violet-50 to-indigo-50 border-violet-200' : 'bg-amber-50 border-amber-200'}`} data-testid="vendor-pricing-card">
              <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">Pricing (USD)</div>
              {localJob.per_word_rate_usd != null ? (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] text-slate-500">Pair</div>
                    <div className="text-sm font-semibold text-slate-900">{localJob.source_language} → {localJob.target_language}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500">Per word</div>
                    <div className="text-sm font-semibold text-slate-900">${localJob.per_word_rate_usd.toFixed(4)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500">Estimated total</div>
                    <div className="text-base font-bold text-violet-700">${localJob.estimated_total_usd != null ? localJob.estimated_total_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-amber-800">{localJob.pricing_message || 'Pricing unavailable for this language pair. Manual review.'}</p>
              )}
            </div>
          )}

          {localJob.notes && (
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
              <div className="text-xs font-medium text-violet-700 mb-1">Client notes</div>
              <p className="text-sm text-slate-700">{localJob.notes}</p>
            </div>
          )}

          {/* Original files */}
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Original Files</h4>
            <div className="space-y-2">
              {original.map(f => (
                <button
                  key={f.id}
                  onClick={() => downloadFileById(f.id)}
                  className="w-full flex items-center justify-between bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl px-4 py-3 text-left"
                  data-testid={`vendor-download-original-${f.id}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-slate-500" />
                    <div>
                      <div className="text-sm font-medium text-slate-900">{f.file_name}</div>
                      <div className="text-xs text-slate-500">{f.estimated_word_count ? `${f.estimated_word_count.toLocaleString()} words` : 'Manual review'}</div>
                    </div>
                  </div>
                  <Download className="w-4 h-4 text-slate-500" />
                </button>
              ))}
            </div>
          </div>

          {/* Status actions */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Status</h4>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleStatusChange('in_review')}
                className="px-3 py-1.5 bg-amber-100 text-amber-700 hover:bg-amber-200 rounded-lg text-xs font-medium"
                data-testid="vendor-set-in-review"
              >Mark In Review</button>
              <button
                onClick={() => handleStatusChange('completed')}
                className="px-3 py-1.5 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg text-xs font-medium"
                data-testid="vendor-set-completed"
              >Mark Completed</button>
            </div>
          </div>

          {/* Client notes panel (also visible in meta block above; left intentionally) */}

          {/* Upload translated */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-3">Upload Translated File(s)</h4>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-violet-300 hover:border-violet-500 bg-violet-50/40 hover:bg-violet-50 rounded-xl py-6 cursor-pointer">
              <Upload className="w-6 h-6 text-violet-500 mb-1" />
              <span className="text-sm font-medium text-slate-700">{uploading ? 'Uploading...' : 'Click to upload translated documents'}</span>
              <span className="text-xs text-slate-500">PDF, DOC, DOCX, TXT</span>
              <input
                type="file"
                multiple
                className="hidden"
                accept=".pdf,.doc,.docx,.txt"
                disabled={uploading}
                onChange={(e) => handleUpload(Array.from(e.target.files || []))}
                data-testid="vendor-translated-upload-input"
              />
            </label>
            {translated.length > 0 && (
              <div className="mt-3 space-y-2">
                {translated.map(f => (
                  <div key={f.id} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4 text-emerald-700" />
                      <span className="font-medium text-slate-900">{f.file_name}</span>
                    </div>
                    <button onClick={() => downloadFileById(f.id)} className="text-emerald-700 hover:text-emerald-800" data-testid={`vendor-download-translated-${f.id}`}>
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Vendor notes */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Vendor Notes</h4>
            {(localJob.vendor_notes || []).length === 0 ? (
              <p className="text-xs text-slate-500">No notes yet.</p>
            ) : (
              <div className="space-y-2 mb-3">
                {(localJob.vendor_notes || []).map(n => (
                  <div key={n.id} className="bg-slate-50 rounded-lg px-3 py-2 text-sm">
                    <div className="text-slate-800">{n.note}</div>
                    <div className="text-[10px] text-slate-500 mt-1">{n.vendor_email} • {new Date(n.created_at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a vendor note..."
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                data-testid="vendor-note-input"
              />
              <button onClick={addNote} disabled={savingNote || !note.trim()} className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium disabled:opacity-50" data-testid="vendor-note-submit">
                {savingNote ? '...' : 'Add'}
              </button>
            </div>
          </div>

          {error && <div className="px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-sm" data-testid="vendor-job-error">{error}</div>}

          {/* Mark as ready */}
          <button
            onClick={markReady}
            disabled={marking || translated.length === 0}
            className="w-full px-5 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl font-semibold text-sm shadow-lg shadow-emerald-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="vendor-mark-ready-btn"
          >
            {marking ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackageCheck className="w-4 h-4" />}
            {translated.length === 0 ? 'Upload a translated file first' : 'Mark as Ready for Download'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Vendor Panel root
// ────────────────────────────────────────────────────────────────
export default function VendorPanel({ user, onLogout }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/translations/vendor/jobs`, { headers: authHeaders() });
      setJobs(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return jobs;
    if (filter === 'open') return jobs.filter(j => !['ready_for_download', 'completed'].includes(j.status));
    if (filter === 'completed') return jobs.filter(j => ['ready_for_download', 'completed'].includes(j.status));
    return jobs;
  }, [jobs, filter]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BrandLogo size={36} />
            <div>
              <div className="text-base font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Law Agency 360</div>
              <div className="text-xs text-slate-500">Legal Translation Vendor Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-xs text-slate-500">Signed in as</div>
              <div className="text-sm font-medium text-slate-900">{user.email}</div>
            </div>
            <button onClick={onLogout} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-rose-600 rounded-xl border border-slate-200 hover:border-rose-200" data-testid="vendor-logout">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-md shadow-violet-500/30">
                <Languages className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Translation Jobs</h1>
            </div>
            <p className="text-sm text-slate-500">Pick up assigned and available human-review jobs, upload translations, and mark ready for client download.</p>
          </div>
          <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1">
            {[
              { id: 'all', label: 'All' },
              { id: 'open', label: 'Open' },
              { id: 'completed', label: 'Completed' }
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${filter === t.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                data-testid={`vendor-filter-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10 text-slate-500">Loading...</div>
        ) : !filtered.length ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="vendor-no-jobs">
            <Languages className="w-10 h-10 text-violet-400 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900">No translation jobs yet</h3>
            <p className="text-sm text-slate-500 mt-1">When clients submit AI + Human Review jobs, they'll show up here.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="vendor-jobs-list">
            {filtered.map(j => (
              <button
                key={j.id}
                onClick={() => setSelected(j)}
                className="w-full text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-violet-300 hover:shadow-md transition-all"
                data-testid={`vendor-job-${j.id.slice(0, 8)}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs font-mono text-slate-500">#{j.id.slice(0, 8)}</span>
                      <StatusBadge status={j.status} />
                      {j.urgency === 'urgent' && (
                        <span className="px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-xs font-medium">Urgent</span>
                      )}
                    </div>
                    <h3 className="text-base font-semibold text-slate-900 mt-2">
                      {j.source_language} <span className="text-slate-400">→</span> {j.target_language}
                    </h3>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                      <span className="font-medium text-slate-700">{j.firm_name || 'Unknown firm'}</span>
                      {j.client_email && (
                        <>
                          <span>•</span>
                          <span>{j.client_email}</span>
                        </>
                      )}
                      <span>•</span>
                      <span className="capitalize">{j.translation_type === 'ai' ? 'AI' : 'AI + Human Review'}</span>
                      <span>•</span>
                      <span>{j.total_word_count ? `${j.total_word_count.toLocaleString()} words` : 'Manual review'}</span>
                      {j.translation_type === 'ai_human' && j.per_word_rate_usd != null && (
                        <>
                          <span>•</span>
                          <span>${j.per_word_rate_usd.toFixed(4)}/word</span>
                          {j.estimated_total_usd != null && (
                            <>
                              <span>•</span>
                              <span className="font-semibold text-violet-700" data-testid={`vendor-job-total-${j.id.slice(0, 8)}`}>${j.estimated_total_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </>
                          )}
                        </>
                      )}
                      <span>•</span>
                      <span>{(j.files || []).length} file(s)</span>
                      <span>•</span>
                      <span>{new Date(j.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <span className="text-xs text-violet-600 font-medium">View →</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>

      {selected && (
        <JobDetail job={selected} onClose={() => setSelected(null)} onUpdated={refresh} />
      )}
    </div>
  );
}
